import Colors from '@dynatrace/strato-design-tokens/colors';

import type { TextAlign, TextBaseline } from './types/canvas.js';
import type { LabelsDisplay, NameAccessor } from './types/treemap.js';
import { AUTO_TEXT_SIZE_PRESETS, SCALE_FALLBACK_COLOR } from '../core/constants.js';
import type { MappedObject } from '../core/types/helpers/mapped-object.js';
import type { TextSize } from '../core/types/text-size.js';

export { AUTO_TEXT_SIZE_PRESETS };

export const DISPLAY_NAME = 'TreeMap';

export const LABELS_DISPLAY = Object.freeze<MappedObject<LabelsDisplay>>({
  All: 'all',
  Clusters: 'clusters',
  ClustersAndValues: 'clusters-and-values',
  Nodes: 'nodes',
  None: 'none',
});

export const TEXT_ALIGN = Object.freeze<MappedObject<TextAlign>>({
  Center: 'center',
  End: 'end',
  Left: 'left',
  Right: 'right',
  Start: 'start',
});

export const TEXT_BASELINE = Object.freeze<MappedObject<TextBaseline>>({
  Alphabetic: 'alphabetic',
  Bottom: 'bottom',
  Hanging: 'hanging',
  Ideographic: 'ideographic',
  Middle: 'middle',
  Top: 'top',
});

export const NAME_ACCESSOR = Object.freeze<MappedObject<NameAccessor>>({
  Name: 'name',
  Value: 'value',
});

export const CLUSTER_OTHER_ID = 'other-cluster';
export const DEFAULT_AGGREGATED_LABEL = 'Other';
export const DEFAULT_ROOT_NODE_NAME = 'root node';
export const DEFAULT_WIDTH = '100%';
export const DEFAULT_HEIGHT = 300;
export const DEFAULT_LOADING_STATE = false;
export const DEFAULT_CATEGORICAL_COLOR_PALETTE = 'categorical';
export const DEFAULT_RANGE_COLOR_PALETTE = 'moss';
export const DEFAULT_MIN_NODE_SIZE = 250;
export const DEFAULT_MIN_NODE_SIZE_PARENT = 1000;
export const DEFAULT_MIN_NODE_SIZE_PARENT_AND_LABELS = 2000;
export const DEFAULT_TOOLBAR_PLACEMENT = 'top-right';

export const DEFAULT_LABELS_DISPLAY: LabelsDisplay = LABELS_DISPLAY.None;
export const DEFAULT_LABEL_ALIGN: TextAlign = TEXT_ALIGN.Center;
export const DEFAULT_LABEL_BASELINE: TextBaseline = TEXT_BASELINE.Middle;
export const DEFAULT_LABEL_FONTSIZE = 12;
export const DEFAULT_LABEL_LINE_HEIGHT = 16;
export const DEFAULT_CLUSTER_LABEL_FONTSIZE = 12;
export const DEFAULT_CLUSTER_LABEL_LINE_HEIGHT = 20;
export const CLUSTER_LABEL_OFFSET_Y = DEFAULT_LABEL_FONTSIZE + 2;
export const CLUSTER_LABEL_OFFSET_X = 10;

const CLUSTER_LABEL_PADDING = 4;
const CLUSTER_LABEL_PADDING_SUM = CLUSTER_LABEL_PADDING * 2;
export const MIN_CLUSTER_HEIGHT =
  DEFAULT_CLUSTER_LABEL_FONTSIZE + CLUSTER_LABEL_PADDING_SUM;
export const DEFAULT_LABEL_PADDING = 4;

export const LABEL_FONT_FAMILY = 'DT Flow';
export const DEFAULT_FONT_WEIGHT = 400;
export const INCREASED_FONT_WEIGHT = 500;
export const DEFAULT_NAME_ACCESSOR = undefined;

export const DEFAULT_TEXT_SIZE: TextSize = DEFAULT_LABEL_FONTSIZE;

export const DARK_LABEL_TEXT = '#000000';
export const LIGHT_LABEL_TEXT = '#ffffff';

export const RECT_BORDER_RADIUS = 2;
export const RECT_STROKE_WIDTH = 1;
export const RECT_CLUSTER_OPACITY = 0.15;

export const NODE_LEAF_PADDING = 1; // sets leaf node padding to 2px
export const NODE_CLUSTER_PADDING = 2; // sets parent node padding to 6px
export const NODE_WITH_RECT_CLUSTER_PADDING = 7;
export const NODE_WITH_RECT_PADDING_TOP =
  DEFAULT_CLUSTER_LABEL_FONTSIZE + CLUSTER_LABEL_PADDING_SUM;
export const ROOT_NODE_WITH_LABELS_OUTER_PADDING = 3;
export const NODE_WITH_RECT_OUTER_PADDING = 3;

const MIN_VISIBLE_NODE_HEIGHT_AND_WIDTH = 1;

const ADDITIONAL_CLUSTER_HEIGHT_SPACING = 2;
const ADDITIONAL_CLUSTER_WIDTH_SPACING = 4;

export const MIN_CLUSTER_HEIGHT_WITH_VISIBLE_CHILDREN =
  DEFAULT_CLUSTER_LABEL_FONTSIZE +
  MIN_VISIBLE_NODE_HEIGHT_AND_WIDTH +
  2 * RECT_STROKE_WIDTH +
  CLUSTER_LABEL_PADDING_SUM +
  ADDITIONAL_CLUSTER_HEIGHT_SPACING;

export const MIN_CLUSTER_WIDTH_WITH_VISIBLE_CHILDREN =
  MIN_VISIBLE_NODE_HEIGHT_AND_WIDTH +
  2 * RECT_STROKE_WIDTH +
  ADDITIONAL_CLUSTER_WIDTH_SPACING;

export const AGGREGATED_NODE_COLOR = Colors.Background.Container.Neutral.Accent;

export const OUT_OF_RANGE_NODE_COLOR =
  Colors.Background.Container.Neutral.Accent;

export const DEFAULT_RANGE_LEGEND_COLOR = SCALE_FALLBACK_COLOR;

export const DEFAULT_BORDER_COLOR = Colors.Border.Neutral.Accent;

export const TREEMAP_CHART_ARIA_LABEL = 'Tree Map Chart';
